import axios from 'axios'
import { BASE_URL, TEST_SCENARIO_ID } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
})

/**
 * REQUEST INTERCEPTOR
 *
 * Attaches JWT token to all outgoing requests.
 * Token is obtained from Jira Connect (AP.context.getToken) or
 * from VITE_DEV_JWT_TOKEN environment variable for local development.
 *
 * The interceptor is async to handle the Promise-based token retrieval.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        // Log warning but don't block request - backend will reject if auth required
        console.warn('API request sent without JWT token')
      }
    } catch (error) {
      // Log error but don't block request - let backend handle auth failure
      console.error('Failed to get JWT token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * RESPONSE INTERCEPTOR
 *
 * Handles global error responses, especially authentication failures.
 * Clears token cache on 401 to force refresh on next request.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log all errors for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    // Handle authentication failures
    if (error.response?.status === 401) {
      console.error('Unauthorized access - clearing token cache')
      // Clear cached token to force refresh on next request
      clearJwtTokenCache()
      // Note: In Jira Connect, we can't redirect - the app runs in an iframe
      // The backend rejection will surface to the user through error handling
    }

    // Pass error to caller (async thunk will handle it)
    return Promise.reject(error)
  }
)

export const fetchTestScenario = async (scenarioId) => {
  try {
    const response = await apiClient.get(`/testcases/testscenario/${scenarioId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test scenario:', error)
throw error
  }
}

/**
 * UPDATE TEST SCENARIO
 *
 * Updates test scenario name, description, priority, and/or assignee
 *
 * @param {number} testScenarioId - ID of test scenario to update
 * @param {object} updates - Fields to update { scenarioName?, description?, priority?, assignee? }
 * @returns {Promise<object>} Updated test scenario data
 */
export const updateTestScenario = async (testScenarioId, updates) => {
  try {
    // Build request body - ONLY include fields that are being updated
    // This prevents overwriting other fields on the backend
    const requestBody = {}

    if (updates.scenarioName !== undefined) {
      requestBody.scenarioName = updates.scenarioName
    }
    if (updates.description !== undefined) {
      requestBody.description = updates.description
    }
    if (updates.priority !== undefined) {
      requestBody.priority = updates.priority
    }
    // Assignee is the Jira accountId string
    if (updates.assignee !== undefined) {
      requestBody.assignee = updates.assignee
    }

    // Note: jiraProjectId is NOT sent for updates - only send fields being changed

    const response = await apiClient.put(
      `/testscenarios/${testScenarioId}`,
      requestBody
    )

    return response.data
  } catch (error) {
    console.error('Failed to update test scenario:', error)
    throw error
  }
}

export const createTestCase = async (testScenarioId, testCaseName, jiraProjectId, description = '', priority = 'Medium') => {
  try {
    const response = await apiClient.post('/testcases', {
      testCaseName,
      description,
      testScenarioId,
      jiraProjectId,
      priority
    })
    return response.data
  } catch (error) {
    console.error('Error creating test case:', error)
    throw error
  }
}

export const updateTestCase = async (testCaseId, updates) => {
  try {
    const response = await apiClient.put(`/testcases/${testCaseId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating test case:', error)
    throw error
  }
}

export const updateTestStep = async (testCaseId, testStepId, updates) => {
  try {
    const response = await apiClient.put(`/testcases/${testCaseId}/teststeps/${testStepId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating test step:', error)
    throw error
  }
}

export const createTestStep = async (testCaseId, stepAction) => {
  try {
    const response = await apiClient.post(`/testcases/${testCaseId}/teststeps`, {
      stepAction
    })
    return response.data
  } catch (error) {
    console.error('Error creating test step:', error)
    throw error
  }
}

export const fetchProjectStatuses = async (projectId) => {
  try {
    const response = await apiClient.get(`/statuses/project/${projectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching project statuses:', error)
    throw error
  }
}

export const updateTestCaseExecution = async (testScenarioId, testRunId, testCaseId, statusId) => {
  try {
    const response = await apiClient.put(
      `/testscenarios/${testScenarioId}/runs/${testRunId}/testcases/${testCaseId}`,
      { statusId }
    )
    return response.data
  } catch (error) {
    console.error('Error updating test case execution:', error)
    throw error
  }
}

export const updateTestStepExecution = async (testScenarioId, testRunId, testStepId, statusId) => {
  try {
    const response = await apiClient.put(
      `/testscenarios/${testScenarioId}/runs/${testRunId}/teststeps/${testStepId}`,
      { statusId }
    )
    return response.data
  } catch (error) {
    console.error('Error updating test step execution:', error)
    throw error
  }
}

/**
 * ============================================================================
 * LABEL API FUNCTIONS
 *
 * Handles fetching available labels, assigned labels, and CRUD operations
 * ============================================================================
 */

/**
 * Fetch all available labels in the project
 * @param {number} projectId - Jira project ID
 * @returns {Promise<Array>} Array of label objects { id, name }
 */
export const fetchProjectLabels = async (projectId) => {
  try {
    const response = await apiClient.get(`/labels/project/${projectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching project labels:', error)
    throw error
  }
}

/**
 * Fetch labels assigned to a specific test scenario
 * @param {number} testScenarioId - Test scenario ID
 * @returns {Promise<Array>} Array of label objects { id, name }
 */
export const fetchTestScenarioLabels = async (testScenarioId) => {
  try {
    const response = await apiClient.get(`/testscenarios/${testScenarioId}/labels`)
    return response.data
  } catch (error) {
    console.error('Error fetching test scenario labels:', error)
    throw error
  }
}

/**
 * Add a label to test scenario
 *
 * Mixed flow: supports adding existing label by ID or creating new label by name
 *
 * @param {number} testScenarioId - Test scenario ID
 * @param {object} labelData - { labelId: <id> } OR { labelName: <name> }
 * @returns {Promise<object>} Created/assigned label object { id, name }
 */
export const addTestScenarioLabel = async (testScenarioId, labelData) => {
  try {
    const response = await apiClient.post(
      `/testscenarios/${testScenarioId}/labels`,
      labelData
    )
    return response.data
  } catch (error) {
    console.error('Error adding test scenario label:', error)
    throw error
  }
}

/**
 * Remove a label from test scenario
 * @param {number} testScenarioId - Test scenario ID
 * @param {number} labelId - Label ID to remove
 * @returns {Promise<void>}
 */
export const removeTestScenarioLabel = async (testScenarioId, labelId) => {
  try {
    const response = await apiClient.delete(
      `/testscenarios/${testScenarioId}/labels/${labelId}`
    )
    return response.data
  } catch (error) {
    console.error('Error removing test scenario label:', error)
    throw error
  }
}

/**
 * Create a new project label
 * If a label with the same name already exists in the project, the existing label is returned.
 *
 * @param {number} projectId - Jira project ID
 * @param {string} name - Label name to create
 * @returns {Promise<object>} Created label object { id, name, projectId, projectName }
 */
export const createProjectLabel = async (projectId, name) => {
  try {
    const response = await apiClient.post(`/labels/project/${projectId}`, { name })
    return response.data
  } catch (error) {
    console.error('Error creating project label:', error)
    throw error
  }
}

/**
 * Update/rename an existing label
 *
 * @param {number} labelId - ID of the label to update
 * @param {string} name - New label name
 * @returns {Promise<object>} Updated label object { id, name, projectId, projectName }
 */
export const updateLabel = async (labelId, name) => {
  try {
    const response = await apiClient.put(`/labels/${labelId}`, { name })
    return response.data
  } catch (error) {
    console.error('Error updating label:', error)
    throw error
  }
}

/**
 * Delete a label permanently
 *
 * @param {number} labelId - ID of the label to delete
 * @returns {Promise<void>}
 */
export const deleteLabel = async (labelId) => {
  try {
    await apiClient.delete(`/labels/${labelId}`)
  } catch (error) {
    console.error('Error deleting label:', error)
    throw error
  }
}

/**
 * Bulk create labels in a project
 * Creates multiple labels at once. Labels that already exist are not duplicated —
 * the existing label is returned for duplicate names.
 *
 * @param {number} projectId - Jira project ID
 * @param {string[]} names - Array of label names to create
 * @returns {Promise<Array>} Array of created label objects { id, name, projectId, projectName }
 */
export const bulkCreateLabels = async (projectId, names) => {
  try {
    const response = await apiClient.post(`/labels/project/${projectId}/bulk`, { names })
    return response.data
  } catch (error) {
    console.error('Error bulk creating labels:', error)
    throw error
  }
}

/**
 * ============================================================================
 * TEST RUN CUSTOM FIELDS API FUNCTIONS
 *
 * Handles fetching custom field definitions and creating test runs
 * ============================================================================
 */

/**
 * Fetch custom fields for test runs in a project
 * Returns field definitions including fieldName, fieldType, orderIndex, showField
 *
 * @param {number} projectId - Jira project ID
 * @returns {Promise<Array>} Array of custom field objects
 */
export const fetchTestRunCustomFields = async (projectId) => {
  try {
    const response = await apiClient.get(`/test-run-custom-fields/project/${projectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test run custom fields:', error)
    throw error
  }
}

/**
 * Create a new custom field for test runs
 * API creates TEXT type fields by default
 *
 * @param {string} fieldName - Name of the new field
 * @param {number} jiraProjectId - Jira project ID
 * @returns {Promise<object>} Created custom field object
 */
export const createCustomField = async (fieldName, jiraProjectId) => {
  try {
    const response = await apiClient.post('/test-run-custom-fields', {
      fieldName,
      jiraProjectId
    })
    return response.data
  } catch (error) {
    console.error('Error creating custom field:', error)
    throw error
  }
}

/**
 * Update a custom field's properties
 * Can update fieldName, showField (visibility), and orderIndex (display order)
 *
 * @param {number} fieldId - ID of the custom field to update
 * @param {number} jiraProjectId - Jira project ID
 * @param {object} updates - Fields to update { fieldName?, showField?, orderIndex? }
 * @returns {Promise<object>} Updated custom field object
 */
export const updateCustomField = async (fieldId, jiraProjectId, updates) => {
  try {
    const response = await apiClient.put(
      `/test-run-custom-fields/project/${jiraProjectId}/${fieldId}`,
      updates
    )
    return response.data
  } catch (error) {
    console.error('Error updating custom field:', error)
    throw error
  }
}

/**
 * Delete a custom field
 * Note: Only non-default fields (isDefault=false) can be deleted
 *
 * @param {number} fieldId - ID of the custom field to delete
 * @param {number} jiraProjectId - Jira project ID
 * @returns {Promise<void>}
 */
export const deleteCustomField = async (fieldId, jiraProjectId) => {
  try {
    await apiClient.delete(`/test-run-custom-fields/project/${jiraProjectId}/${fieldId}`)
  } catch (error) {
    console.error('Error deleting custom field:', error)
    throw error
  }
}

/**
 * Create a new test run for a test scenario
 * Field values are passed as key-value pairs where keys are fieldName
 *
 * @param {number} testScenarioId - Test scenario ID
 * @param {object} runData - Object with field names as keys and values as strings
 * @returns {Promise<object>} Created test run object
 */
export const createTestRun = async (testScenarioId, runData) => {
  try {
    const response = await apiClient.post(
      `/testscenarios/${testScenarioId}/runs`,
      runData
    )
    return response.data
  } catch (error) {
    console.error('Error creating test run:', error)
    throw error
  }
}

/**
 * UPDATE TEST RUN
 *
 * Updates field values for an existing test run.
 *
 * @param {number} testScenarioId - The test scenario ID
 * @param {number} testRunId - The test run ID to update
 * @param {object} updates - Object with field names as keys and values to update
 * @returns {Promise<object>} Updated test run object
 */
export const updateTestRun = async (testScenarioId, testRunId, updates) => {
  try {
    const response = await apiClient.put(
      `/testscenarios/${testScenarioId}/runs/${testRunId}`,
      updates
    )
    return response.data
  } catch (error) {
    console.error('Error updating test run:', error)
    throw error
  }
}

/**
 * DELETE TEST RUN
 *
 * Permanently deletes a test run and all associated data.
 * This includes all execution statuses, audit logs, and linked bugs.
 *
 * @param {number} testScenarioId - The test scenario ID
 * @param {number} testRunId - The test run ID to delete
 * @returns {Promise<void>} - Returns 204 No Content on success
 */
export const deleteTestRun = async (testScenarioId, testRunId) => {
  try {
    await apiClient.delete(`/testscenarios/${testScenarioId}/runs/${testRunId}`)
  } catch (error) {
    console.error('Error deleting test run:', error)
    throw error
  }
}

/**
 * CLEAR TEST RUN
 *
 * Resets all execution statuses in a run to null/empty.
 * Preserves the run structure but clears all test results.
 *
 * @param {number} testScenarioId - The test scenario ID
 * @param {number} testRunId - The test run ID to clear
 * @returns {Promise<void>} - Returns 200 OK on success
 */
export const clearTestRun = async (testScenarioId, testRunId) => {
  try {
    await apiClient.post(`/testscenarios/${testScenarioId}/runs/${testRunId}/clear`)
  } catch (error) {
    console.error('Error clearing test run:', error)
    throw error
  }
}

/**
 * DELETE TEST CASE
 *
 * Deletes a test case and all its associated test steps.
 *
 * @param {number} testCaseId - ID of the test case to delete
 * @returns {Promise<void>}
 */
export const deleteTestCase = async (testCaseId) => {
  try {
    const response = await apiClient.delete(`/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting test case:', error)
    throw error
  }
}

/**
 * DELETE TEST STEP
 *
 * Deletes a test step from a test case.
 *
 * @param {number} testCaseId - ID of the parent test case
 * @param {number} testStepId - ID of the test step to delete
 * @returns {Promise<void>}
 */
export const deleteTestStep = async (testCaseId, testStepId) => {
  try {
    const response = await apiClient.delete(`/testcases/${testCaseId}/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting test step:', error)
    throw error
  }
}
